import * as React from 'react';
import dayjs, {Dayjs} from 'dayjs';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import {UseDateFieldProps} from '@mui/x-date-pickers/DateField';
import {
  BaseSingleInputFieldProps,
  DateValidationError,
  FieldSection,
} from '@mui/x-date-pickers/models';
import {IconButton} from '@mui/material';
import {Image} from '@jobseeker/lotus/components/Image';
import {ChevronDown, HiristCalendar} from '@jobseeker/lotus/assets/icons';
import weekday from 'dayjs/plugin/weekday';
import updateLocale from 'dayjs/plugin/updateLocale';

dayjs.extend(weekday);
dayjs.extend(updateLocale);

interface ButtonFieldProps
  extends UseDateFieldProps<Dayjs, false>,
    BaseSingleInputFieldProps<
      Dayjs | null,
      Dayjs,
      FieldSection,
      false,
      DateValidationError
    > {
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

dayjs.updateLocale('en', {
  weekdays: [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ],
  weekdaysShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  weekdaysMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
  weekStart: 1,
});

function ButtonField(props: ButtonFieldProps) {
  const {
    setOpen,
    // label,
    id,
    disabled,
    InputProps: {ref} = {},
    inputProps: {'aria-label': ariaLabel} = {},
  } = props;

  return (
    <IconButton
      ref={ref}
      onClick={() => setOpen?.(prev => !prev)}
      aria-label={ariaLabel}
      id={id}
      sx={{padding: 0}}
      disabled={disabled}
    >
      <Image src={HiristCalendar} alt="calendar" height={20} width={20} />
    </IconButton>
  );
}

const CustomIcon: React.FC = () => (
  <Image
    src={ChevronDown}
    alt="dropdown-icon"
    width={12}
    height={12}
    // style={{
    //   marginRight: '16px',
    //   transform: open ? 'rotate(180deg)' : 'rotate(0deg)', // Rotate on open
    //   transition: 'transform 0.3s ease', // Smooth rotation
    //   cursor: 'pointer',
    // }}
  />
);

export default function PickerWithButtonField({
  handleDateChange,
}: {
  handleDateChange: (date: Date, subtract?: boolean) => void;
}) {
  const [value, setValue] = React.useState<Dayjs>();
  const [open, setOpen] = React.useState(false);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
      <DatePicker
        label={value == null ? null : value.format('MM/DD/YYYY')}
        value={value}
        onChange={newValue => {
          setValue(newValue ?? dayjs());
          handleDateChange(
            newValue?.add(5.5, 'hours')?.toDate() ?? new Date(),
            false
          );
        }}
        slots={{
          field: ButtonField,
          switchViewIcon: CustomIcon,
        }}
        slotProps={{
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          field: {setOpen} as any,
          popper: {
            sx: {
              '@media (min-width:900px)': {
                '& .MuiPaper-root': {
                  marginTop: 2.5,
                  borderRadius: '16px',
                  padding: 0.5,
                  border: '1px solid rgba(126, 126, 126, 0.20)',
                },
                '& .MuiDateCalendar-root': {
                  width: '100%',
                },
              },
            },
          },
        }}
        desktopModeMediaQuery="@media (min-width:900px)"
        showDaysOutsideCurrentMonth={true}
        open={open}
        onClose={() => setOpen(false)}
        onOpen={() => setOpen(true)}
        disablePast
        maxDate={dayjs().add(30, 'days')}
      />
    </LocalizationProvider>
  );
}
